import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import 'react-markdown-editor-lite/lib/index.css';

import BreadCrumb from '../../components/BreadCrumb';
import DefaultCreationForm, {
  DefaultCreationFormButtonGroup,
  DefaultCreationFormGroup,
} from '../../components/DefaultCreationForm';
import { DefaultPageTitle } from '../../components/DefaultPageTitle';
import DefaultButton from '../../components/DefaultButton';
import Swal from 'sweetalert2';
import getErrorMessage from '../../helpers/get-error-message';
import {
  getPageContent as getPageContentService,
  updateAboutHalu as updateAboutHaluService,
} from '../../services/aboutHalu';
import { EditorState } from 'draft-js';
import { DescriptionTextarea, getCleanHtml } from './Editor';
import checkEmptyString from '../../helpers/check-empty-string';
import { hideModal, showModal } from '../../helpers/modal';
import SelectSambaVideosContent from '../../components/SelectSambaVideosContent';
import MediaFromResponse from '../../models/from-api-response/media';

import { TermsContainer, ContentUploadOrSelectContainer, ButtonGroupInline } from './styles';
import SelectSubtitles from '../../components/SelectSubtitles';

const AboutHalu: React.FC = () => {
  const [pageId, setPageId] = useState('');

  const [descriptionState, setDescriptionState] = useState(EditorState.createEmpty());
  const [descriptionText, setDescriptionText] = useState('');
  const cleanDescriptionHTML = getCleanHtml(descriptionState);

  const [reference, setReference] = useState('');
  const [hasSubtitle, setHasSubtitle] = useState(false);

  const getPageContent = async () => {
    try {
      const currentContent = await getPageContentService();
      setDescriptionText(currentContent[0].text || '');
      setPageId(currentContent[0].home_id);
      setReference(currentContent[0].info.mediaUrl);
    } catch (error) {
      Swal.fire({
        title: 'Erro',
        text: 'Ocorreu um erro ao recuperar os dados da página!',
        icon: 'error',
      });
    }
  };

  const updatePageDescription = async (event: React.FormEvent) => {
    if (event.preventDefault) {
      event.preventDefault();
    }

    if (checkEmptyString(cleanDescriptionHTML)) {
      throw new Error('Preencha todos os campos.');
    }

    try {
      const info = { mediaUrl: reference };

      await updateAboutHaluService(pageId, cleanDescriptionHTML, info);

      Swal.fire({
        title: 'Sucesso!',
        text: 'Conteúdo editado com sucesso.',
        icon: 'success',
      });
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      Swal.fire({ icon: 'error', text: 'Erro ao alterar conteúdo da página. ' + errorMessage });
    }
  };

  const onSelectContent = (sambaVideosContent: MediaFromResponse) => {
    if (sambaVideosContent) {
      if (sambaVideosContent.files && sambaVideosContent.files.length) {
        const firstFile = sambaVideosContent.files[0];

        if (firstFile) {
          const playerKey = process.env.REACT_APP_PLAYER_KEY;
          const referenceUrl = `${process.env.REACT_APP_PLAYER_INITIAL_URL}/${playerKey}/${sambaVideosContent.id}`;

          setReference(referenceUrl);
        }
      }
      setHasSubtitle(sambaVideosContent.captions.length > 0);
    }

    hideModal();
  };

  const selectContent = () => {
    showModal(`Selecionar Vídeo de apresentação`, <SelectSambaVideosContent onSelectContent={onSelectContent} />);
  };

  const selectSubtitles = () => {
    showModal('Selecionar Legenda', <SelectSubtitles reference={reference} />);
  };

  useEffect(() => {
    getPageContent();
  }, []);

  return (
    <TermsContainer>
      <BreadCrumb crumbs={[<Link to="/profile">Dashboard</Link>, <span>Cms</span>, <span>Sobre Halu Gamashi</span>]} />

      <DefaultPageTitle>Sobre Halu Gamashi</DefaultPageTitle>

      <DefaultCreationForm onSubmit={updatePageDescription}>
        <DefaultCreationFormGroup>
          <label className="required" htmlFor="reference">
            Vídeo de apresentação
          </label>

          <ContentUploadOrSelectContainer>
            <ButtonGroupInline>
              <DefaultButton type="button" onClick={selectContent}>
                Selecionar vídeo
              </DefaultButton>
              {reference && !hasSubtitle && (
                <DefaultButton type="button" onClick={selectSubtitles}>
                  Selecionar Legenda
                </DefaultButton>
              )}
            </ButtonGroupInline>
            {reference && reference.startsWith(process.env.REACT_APP_PLAYER_INITIAL_URL || '') ? (
              <iframe title="referenced-video" allowFullScreen src={reference} frameBorder={0}></iframe>
            ) : (
              <></>
            )}
          </ContentUploadOrSelectContainer>
        </DefaultCreationFormGroup>

        <DefaultCreationFormGroup>
          <label className="required" htmlFor="reference">
            Descrição
          </label>
          <DescriptionTextarea
            description={descriptionText}
            editorState={descriptionState}
            setEditorState={setDescriptionState}
          />
        </DefaultCreationFormGroup>

        <DefaultCreationFormButtonGroup>
          <DefaultButton className="success">Salvar</DefaultButton>
        </DefaultCreationFormButtonGroup>
      </DefaultCreationForm>
    </TermsContainer>
  );
};

export default AboutHalu;
